import React, { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useSlotAssetGeneratorStore } from '../../store/slotAssetGeneratorStore';

interface SlotGeneratingViewProps {
  onCancel: () => void;
}

export const SlotGeneratingView: React.FC<SlotGeneratingViewProps> = ({ onCancel }) => {
  const generation = useSlotAssetGeneratorStore((s) => s.generation);
  const { request, progress } = generation;
  const [elapsed, setElapsed] = useState(0);
  const startTimeRef = useRef(Date.now());

  useEffect(() => {
    startTimeRef.current = Date.now();
    const interval = setInterval(() => {
      setElapsed(Date.now() - startTimeRef.current);
    }, 100);
    return () => clearInterval(interval);
  }, []);

  const percentage = progress.total > 0
    ? Math.round((progress.completed / progress.total) * 100)
    : 0;

  const elapsedSec = (elapsed / 1000).toFixed(1);
  const estimatedTotal = progress.completed > 0
    ? (elapsed / progress.completed) * progress.total
    : 0;
  const remainingSec = estimatedTotal > 0
    ? Math.max(0, (estimatedTotal - elapsed) / 1000).toFixed(0)
    : '...';

  return (
    <div className="flex-1 flex flex-col p-4 gap-4">
      {request && (
        <div className="opacity-40 pointer-events-none text-xs space-y-1 bg-zinc-800/50 rounded p-3 border border-zinc-700/50">
          <div className="font-medium text-zinc-300">{request.component.name}</div>
          <div className="text-zinc-500 line-clamp-2">{request.prompt}</div>
        </div>
      )}

      <div className="flex-1 flex flex-col items-center justify-center gap-6">
        <div className="relative">
          <Loader2 size={48} className="text-blue-400 animate-spin" />
        </div>

        <div className="text-center space-y-2">
          <div className="text-sm text-zinc-200 font-medium">
            Generating... {progress.completed} / {progress.total} images
          </div>

          <div className="w-64 h-2 bg-zinc-800 rounded-full overflow-hidden border border-zinc-700">
            <div
              className="h-full bg-blue-500 rounded-full"
              style={{
                width: `${percentage}%`,
                transition: 'width 0.3s ease',
              }}
            />
          </div>

          <div className="text-[11px] text-zinc-500 space-x-3">
            <span>Elapsed: {elapsedSec}s</span>
            <span>|</span>
            <span>Remaining: ~{remainingSec}s</span>
          </div>
        </div>
      </div>

      <button
        onClick={onCancel}
        className="w-full py-2 rounded-lg border border-zinc-600 text-zinc-300 hover:bg-zinc-800 text-sm transition-colors"
      >
        Cancel
      </button>
    </div>
  );
};
